/**
 * NexusCodeLanding — Página de marketing do NEXUS CODE (nexuscodes.org).
 *
 * Estrutura:
 *   1. Nav sticky: logo | Features | Pricing | FAQs | Enterprise | CTA
 *   2. Hero: full-screen dark + headline + CTA
 *   3. Features: accordion + terminal mockup
 *   4. Pricing: toggle Mensal/Anual + 3 cards (Free / Standard / Pro)
 *   5. FAQ accordion
 *   6. CTA banner final
 *   7. Footer 4 colunas
 */
import React, { useState } from "react";

// ── Paleta ────────────────────────────────────────────────────────────────────
const C = {
  bg:      "#030407",
  surface: "#0d1117",
  border:  "#1e293b",
  accent:  "#6366f1",
  accentH: "#818cf8",
  yellow:  "#f59e0b",
  green:   "#22c55e",
  text:    "#f1f5f9",
  muted:   "#64748b",
  muted2:  "#94a3b8",
};

// ── Dados ─────────────────────────────────────────────────────────────────────
const FEATURES = [
  {
    icon: "⚡",
    title: "Build código em segundos",
    body:
      "Descreve o que queres construir em linguagem natural. O NEXUS CODE planeia, escreve e testa o código automaticamente no teu repositório GitHub.",
    terminal: [
      { t: "user",  msg: "Cria uma API REST de autenticação com JWT" },
      { t: "agent", msg: "→ Planeando arquitectura…" },
      { t: "agent", msg: "→ Criando routes/auth.py (43 linhas)" },
      { t: "agent", msg: "→ Adicionando middleware JWT" },
      { t: "agent", msg: "→ Escrevendo testes unitários" },
      { t: "ok",    msg: "✓ 5 ficheiros criados · branch: feature/auth-jwt" },
    ],
  },
  {
    icon: "🔗",
    title: "Conecta o teu GitHub",
    body:
      "Liga o teu repositório em segundos. O agente trabalha numa branch isolada — nunca toca na main sem a tua aprovação explícita.",
    terminal: [
      { t: "user",  msg: "github_token + owner/repo conectados" },
      { t: "agent", msg: "→ Verificando acesso ao repositório…" },
      { t: "agent", msg: "→ Criando branch: feature/nexus-build-1" },
      { t: "agent", msg: "→ Lendo estrutura do projecto" },
      { t: "ok",    msg: "✓ Workspace pronto · 0 alterações na main" },
    ],
  },
  {
    icon: "🛡️",
    title: "Approval gate — tu controlas",
    body:
      "Cada escrita de ficheiro requer a tua aprovação. Vês o diff completo antes de qualquer commit. Podes rejeitar, editar ou aprovar linha a linha.",
    terminal: [
      { t: "agent", msg: "→ Propondo alteração: src/models/user.py" },
      { t: "diff",  msg: "+ campo: refresh_token: Optional[str]" },
      { t: "diff",  msg: "+ campo: last_login: datetime" },
      { t: "user",  msg: "✓ Aprovado" },
      { t: "ok",    msg: "✓ Commit criado na branch feature/" },
    ],
  },
  {
    icon: "🚀",
    title: "Deploy assistido",
    body:
      "Quando estiveres pronto, o NEXUS CODE guia-te pelo processo de merge e deploy. Suporta Vercel, Railway, Render e qualquer CI/CD.",
    terminal: [
      { t: "agent", msg: "→ Abrindo Pull Request no GitHub" },
      { t: "agent", msg: "→ Resumo: 3 features · 0 breaking changes" },
      { t: "agent", msg: "→ Todos os testes passaram (GitHub Actions)" },
      { t: "ok",    msg: "✓ PR #42 pronto para merge" },
    ],
  },
];

const FAQS = [
  {
    q: "O que são créditos NEXUS CODE?",
    a: "Créditos são a unidade de consumo da plataforma. Cada acção do agente (perguntar, analisar, editar ficheiro, criar feature) custa um número diferente de créditos. O plano Free inclui 5 créditos de bónus no registo para experimentares.",
  },
  {
    q: "O agente tem acesso à minha branch main?",
    a: "Nunca. O NEXUS CODE cria sempre uma branch isolada (ex: feature/nexus-build-123). Cada escrita de ficheiro requer a tua aprovação explícita antes de qualquer commit. A main fica sempre protegida.",
  },
  {
    q: "Posso conectar múltiplos repositórios?",
    a: "No plano Standard e Pro podes criar múltiplos workspaces com repositórios diferentes. O plano Free permite 1 repositório.",
  },
  {
    q: "Que linguagens e frameworks são suportados?",
    a: "O NEXUS CODE é agnóstico de linguagem — funciona com qualquer repositório GitHub. Python, TypeScript, React, Node, Go, Rust, Java… se o GitHub suporta, o NEXUS CODE trabalha.",
  },
  {
    q: "Os meus dados e código são privados?",
    a: "Sim. O agente acede ao teu repositório com o token que forneces, processado em memória durante a sessão. Não armazenamos o conteúdo dos teus ficheiros, apenas o histórico de mensagens da conversa.",
  },
  {
    q: "Posso cancelar a assinatura a qualquer momento?",
    a: "Sim, sem compromissos. Cancelas quando quiseres e mantens o acesso até ao fim do período pago. Os créditos avulsos comprados não expiram.",
  },
];

// ── Estilos inline reutilizáveis ──────────────────────────────────────────────
const S = {
  nav: {
    position: "fixed", top: 0, left: 0, right: 0, zIndex: 100,
    background: "rgba(3,4,7,0.85)", backdropFilter: "blur(12px)",
    borderBottom: `1px solid ${C.border}`,
    display: "flex", alignItems: "center", justifyContent: "space-between",
    padding: "0 32px", height: 60,
  },
  navLogo: {
    fontSize: 18, fontWeight: 700, color: C.text, letterSpacing: "-0.5px",
    cursor: "pointer",
  },
  navLinks: { display: "flex", gap: 28, alignItems: "center" },
  navLink: {
    fontSize: 13, color: C.muted2, cursor: "pointer", fontWeight: 500,
    transition: "color 0.15s",
  },
  btn: (variant = "primary") => ({
    padding: "9px 20px", borderRadius: 8, fontWeight: 600,
    fontSize: 13, cursor: "pointer", border: "none", outline: "none",
    transition: "all 0.15s",
    ...(variant === "primary"
      ? { background: C.accent, color: "#fff" }
      : variant === "outline"
      ? { background: "transparent", color: C.text, border: `1px solid ${C.border}` }
      : { background: C.yellow, color: "#000" }),
  }),
  section: (id) => ({
    padding: "96px 0 80px",
    maxWidth: 1100, margin: "0 auto", paddingLeft: 32, paddingRight: 32,
  }),
  sectionLabel: {
    display: "inline-block", fontSize: 11, fontWeight: 700,
    color: C.accent, letterSpacing: 2, textTransform: "uppercase",
    marginBottom: 12,
  },
  h2: {
    fontSize: 36, fontWeight: 800, color: C.text,
    lineHeight: 1.2, letterSpacing: "-0.5px",
    margin: "0 0 16px",
  },
  body: { fontSize: 15, color: C.muted2, lineHeight: 1.7, maxWidth: 520 },
  terminal: {
    background: "#0a0f1a", border: `1px solid ${C.border}`,
    borderRadius: 12, overflow: "hidden",
    fontFamily: "'JetBrains Mono', 'Fira Code', monospace", fontSize: 12,
  },
  termHeader: {
    background: "#111827", padding: "10px 16px",
    display: "flex", alignItems: "center", gap: 6,
    borderBottom: `1px solid ${C.border}`,
  },
  dot: (color) => ({ width: 10, height: 10, borderRadius: "50%", background: color }),
  termBody: { padding: "16px 20px", display: "flex", flexDirection: "column", gap: 8 },
};

// ── Sub-componentes ───────────────────────────────────────────────────────────

function TerminalMockup({ lines }) {
  return (
    <div style={S.terminal}>
      <div style={S.termHeader}>
        <div style={S.dot("#ef4444")} />
        <div style={S.dot("#f59e0b")} />
        <div style={S.dot("#22c55e")} />
        <span style={{ marginLeft: 8, color: C.muted, fontSize: 11 }}>nexus-agent</span>
      </div>
      <div style={S.termBody}>
        {lines.map((l, i) => (
          <div key={i} style={{
            color: l.t === "user" ? C.accentH
              : l.t === "ok" ? C.green
              : l.t === "diff" ? "#86efac"
              : C.muted2,
            lineHeight: 1.5,
          }}>
            {l.t === "user" && <span style={{ color: C.accent }}>▸ </span>}
            {l.t === "diff" && <span style={{ color: "#4ade80" }}>  </span>}
            {l.msg}
          </div>
        ))}
      </div>
    </div>
  );
}

function PlanCard({ plan, billing, onCta }) {
  const price = billing === "annual" ? plan.price_year : plan.price_month;
  const save  = billing === "annual" && plan.price_month > 0
    ? (plan.price_month - plan.price_year) * 12
    : null;

  return (
    <div style={{
      flex: 1, minWidth: 260,
      background: plan.highlight ? `linear-gradient(135deg,#1a0a3e,#0d0f1c)` : C.surface,
      border: `1px solid ${plan.highlight ? C.accent : C.border}`,
      borderRadius: 20, padding: "32px 28px",
      display: "flex", flexDirection: "column",
      boxShadow: plan.highlight ? `0 0 0 1px ${C.accent}44, 0 8px 32px ${C.accent}18` : "none",
    }}>
      {/* Header */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 6 }}>
        <span style={{ fontSize: 22, fontWeight: 700, color: C.text }}>{plan.name}</span>
        {billing === "annual" && plan.price_month > 0 && (
          <span style={{
            display: "flex", alignItems: "center", gap: 5,
            background: "#1e3a8a", color: "#93c5fd",
            fontSize: 11, fontWeight: 700, padding: "3px 10px", borderRadius: 99,
          }}>
            <span style={{ width: 6, height: 6, borderRadius: "50%", background: "#3b82f6", display: "inline-block" }}/>
            Annual
          </span>
        )}
      </div>
      <div style={{ fontSize: 13, color: C.muted, marginBottom: 24 }}>{plan.description}</div>

      {/* Price */}
      <div style={{ display: "flex", alignItems: "baseline", gap: 6, flexWrap: "wrap", marginBottom: 4 }}>
        <span style={{ fontSize: 52, fontWeight: 800, color: C.text, lineHeight: 1 }}>€{price}</span>
        <span style={{ color: C.muted, fontSize: 14 }}>/mês</span>
        {save && (
          <span style={{
            marginLeft: 6, background: "#1e3a8a", color: "#93c5fd",
            fontSize: 11, fontWeight: 700, padding: "3px 10px", borderRadius: 99,
          }}>
            Poupa €{save}
          </span>
        )}
      </div>

      <div style={{ height: 1, background: C.border, margin: "24px 0" }} />

      {/* Inherit label */}
      {plan.inherit && (
        <div style={{ fontSize: 12, color: C.muted, marginBottom: 14 }}>
          Tudo no {plan.inherit}, mais:
        </div>
      )}

      {/* Features */}
      <ul style={{ listStyle: "none", padding: 0, margin: "0 0 32px", flex: 1, display: "flex", flexDirection: "column", gap: 11 }}>
        {plan.features.map((f, i) => (
          <li key={i} style={{ display: "flex", alignItems: "center", gap: 10, fontSize: 13.5, color: C.muted2 }}>
            <span style={{ color: C.green, fontWeight: 700, fontSize: 15, flexShrink: 0 }}>✓</span> {f}
          </li>
        ))}
      </ul>

      {/* CTA */}
      <button
        onClick={onCta}
        style={{
          width: "100%", padding: "14px 0", borderRadius: 99,
          fontSize: 14, fontWeight: 700, cursor: "pointer", border: "none",
          background: plan.highlight ? C.accent : "#11131f",
          color: "#fff",
          transition: "opacity 0.15s",
        }}
        onMouseOver={e => e.currentTarget.style.opacity = "0.85"}
        onMouseOut={e => e.currentTarget.style.opacity = "1"}
      >
        {plan.cta}
      </button>
    </div>
  );
}

function FaqItem({ q, a }) {
  const [open, setOpen] = useState(false);
  return (
    <div style={{
      borderBottom: `1px solid ${C.border}`, padding: "20px 0", cursor: "pointer",
    }} onClick={() => setOpen(o => !o)}>
      <div style={{
        display: "flex", justifyContent: "space-between", alignItems: "center",
        fontSize: 15, fontWeight: 600, color: C.text,
      }}>
        {q}
        <span style={{ color: C.muted, fontSize: 18, transform: open ? "rotate(45deg)" : "none", transition: "0.2s" }}>
          +
        </span>
      </div>
      {open && (
        <div style={{ fontSize: 14, color: C.muted2, lineHeight: 1.7, marginTop: 12 }}>{a}</div>
      )}
    </div>
  );
}

// ── Componente principal ──────────────────────────────────────────────────────

export default function NexusCodeLanding({ onGetStarted }) {
  const [featureIdx, setFeatureIdx] = useState(0);
  const [billing,    setBilling]    = useState("monthly"); // "monthly" | "annual"
  const [mobileMenu, setMobileMenu] = useState(false);

  const PLANS = [
    {
      name: "Free", price_month: 0, price_year: 0, highlight: false, inherit: null,
      description: "Para começar e explorar sem custo",
      cta: "Começar grátis",
      features: ["5 créditos de bónus no registo", "1 repositório GitHub", "Approval gate obrigatório", "Suporte por email"],
    },
    {
      name: "Standard", price_month: 20, price_year: 17, highlight: false, inherit: "Free",
      description: "Perfeito para builders individuais",
      cta: "Escolher Standard",
      features: ["25 créditos / mês", "Repositórios ilimitados", "Integração GitHub completa", "Preview automático", "Branch isolada sempre", "Histórico 90 dias"],
    },
    {
      name: "Pro", price_month: 49, price_year: 40, highlight: true, inherit: "Standard",
      description: "Para equipas e uso intensivo",
      cta: "Escolher Pro",
      features: ["65 créditos / mês", "Multi-agent pipeline (Planner + Dev + QA)", "Deploy assistido", "Janela de contexto 200K tokens", "Suporte prioritário", "Créditos extra com desconto"],
    },
  ];

  const currentFeature = FEATURES[featureIdx];

  const scrollTo = (id) => {
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <div style={{ background: C.bg, color: C.text, fontFamily: "'Manrope', system-ui, sans-serif", minHeight: "100vh" }}>
      {/* Mobile responsive styles */}
      <style>{`
        @media (max-width: 640px) {
          .nc-nav-links { display: none !important; }
          .nc-nav-hamburger { display: block !important; }
        }
        @media (min-width: 641px) {
          .nc-nav-hamburger { display: none !important; }
        }
      `}</style>

      {/* ── Nav ── */}
      <nav style={S.nav}>
        <div style={{ ...S.navLogo, display: "flex", alignItems: "center", gap: 8 }} onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}>
          <svg width="28" height="28" viewBox="0 0 38 38" fill="none" xmlns="http://www.w3.org/2000/svg">
            <defs>
              <linearGradient id="ncNavBg" x1="0" y1="0" x2="38" y2="38" gradientUnits="userSpaceOnUse">
                <stop offset="0%" stopColor="#2d0a6e"/>
                <stop offset="100%" stopColor="#7c3aed"/>
              </linearGradient>
              <filter id="ncNavGlow" x="-50%" y="-50%" width="200%" height="200%">
                <feGaussianBlur stdDeviation="1.2" result="blur"/>
                <feMerge><feMergeNode in="blur"/><feMergeNode in="SourceGraphic"/></feMerge>
              </filter>
            </defs>
            <rect width="38" height="38" rx="11" fill="url(#ncNavBg)"/>
            <line x1="10" y1="10" x2="10" y2="28" stroke="white" strokeWidth="2.6" strokeLinecap="round" filter="url(#ncNavGlow)"/>
            <line x1="28" y1="10" x2="28" y2="28" stroke="white" strokeWidth="2.6" strokeLinecap="round" filter="url(#ncNavGlow)"/>
            <line x1="10" y1="10" x2="28" y2="28" stroke="#c4b5fd" strokeWidth="2.2" strokeLinecap="round" filter="url(#ncNavGlow)"/>
            <circle cx="10" cy="10" r="2.8" fill="#e9d5ff"/>
            <circle cx="10" cy="28" r="2.8" fill="#e9d5ff"/>
            <circle cx="28" cy="10" r="2.8" fill="#e9d5ff"/>
            <circle cx="28" cy="28" r="2.8" fill="#e9d5ff"/>
            <circle cx="19" cy="19" r="1.6" fill="#a78bfa" opacity="0.8"/>
          </svg>
          NEXUS <span style={{ color: C.accent }}>&nbsp;CODE</span>
        </div>

        {/* Desktop links */}
        <div style={{ ...S.navLinks, "@media(max-width:640px)": { display: "none" } }} className="nc-nav-links">
          {[["Features","features"],["Pricing","pricing"],["FAQs","faqs"],["Enterprise","enterprise"]].map(([label, id]) => (
            <span
              key={id}
              style={S.navLink}
              onClick={() => scrollTo(id)}
              onMouseEnter={e => e.target.style.color = C.text}
              onMouseLeave={e => e.target.style.color = C.muted2}
            >
              {label}
            </span>
          ))}
          <button style={S.btn("primary")} onClick={onGetStarted}>
            Começar grátis →
          </button>
        </div>

        {/* Mobile hamburger */}
        <button
          className="nc-nav-hamburger"
          onClick={() => setMobileMenu(m => !m)}
          style={{
            background: "none", border: `1px solid ${C.border}`, borderRadius: 6,
            color: C.text, fontSize: 18, padding: "4px 10px", cursor: "pointer",
            display: "none",
          }}
          aria-label="Menu"
        >
          {mobileMenu ? "✕" : "☰"}
        </button>
      </nav>

      {/* Mobile menu drawer */}
      {mobileMenu && (
        <div style={{
          position: "fixed", top: 60, left: 0, right: 0,
          background: C.surface, borderBottom: `1px solid ${C.border}`,
          zIndex: 99, padding: "16px 24px", display: "flex", flexDirection: "column", gap: 16,
        }}>
          {[["Features","features"],["Pricing","pricing"],["FAQs","faqs"],["Enterprise","enterprise"]].map(([label, id]) => (
            <span key={id} style={{ fontSize: 15, color: C.muted2, cursor: "pointer", fontWeight: 500 }}
              onClick={() => { scrollTo(id); setMobileMenu(false); }}>
              {label}
            </span>
          ))}
          <button style={{ ...S.btn("primary"), width: "100%", padding: "12px 0", fontSize: 14 }}
            onClick={() => { setMobileMenu(false); onGetStarted(); }}>
            Começar grátis →
          </button>
        </div>
      )}

      {/* ── Hero ── */}
      <section style={{
        minHeight: "100vh", display: "flex", flexDirection: "column",
        alignItems: "center", justifyContent: "center",
        textAlign: "center", padding: "120px 32px 80px",
        background: `radial-gradient(ellipse 80% 60% at 50% 0%, ${C.accent}18 0%, transparent 70%)`,
        position: "relative",
      }}>
        {/* Pill badge */}
        <div style={{
          display: "inline-flex", alignItems: "center", gap: 8,
          background: `${C.accent}18`, border: `1px solid ${C.accent}44`,
          borderRadius: 99, padding: "6px 16px", marginBottom: 32,
          fontSize: 12, color: C.accentH, fontWeight: 600,
        }}>
          <span style={{ width: 6, height: 6, background: C.green, borderRadius: "50%", display: "inline-block" }} />
          Powered by Claude · Multi-agent pipeline
        </div>

        <h1 style={{
          fontSize: "clamp(36px, 6vw, 72px)", fontWeight: 900,
          lineHeight: 1.1, letterSpacing: "-2px",
          margin: "0 0 24px",
          maxWidth: 760,
        }}>
          Começa a construir no{" "}
          <span style={{
            background: `linear-gradient(135deg, ${C.accent}, ${C.accentH})`,
            WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent",
          }}>
            NEXUS CODE
          </span>{" "}
          hoje.
        </h1>

        <p style={{
          fontSize: 18, color: C.muted2, lineHeight: 1.7,
          maxWidth: 560, margin: "0 0 40px",
        }}>
          O agente de IA que escreve código no teu repositório GitHub —
          com approval gate, branches isoladas e deploy assistido.
          Tu decides o que entra na main.
        </p>

        <div style={{ display: "flex", gap: 12, flexWrap: "wrap", justifyContent: "center" }}>
          <button
            style={{ ...S.btn("primary"), padding: "14px 32px", fontSize: 15, fontWeight: 700, borderRadius: 10 }}
            onClick={onGetStarted}
          >
            Começar grátis — 5 créditos incluídos
          </button>
          <button
            style={{ ...S.btn("outline"), padding: "14px 28px", fontSize: 15, borderRadius: 10 }}
            onClick={() => scrollTo("features")}
          >
            Ver como funciona
          </button>
        </div>

        {/* Stats */}
        <div style={{
          display: "flex", gap: 48, marginTop: 64, flexWrap: "wrap", justifyContent: "center",
        }}>
          {[
            ["Multi-agent", "Planner + Dev + QA"],
            ["GitHub nativo", "Branches isoladas"],
            ["Approval gate", "Tu controlas o commit"],
          ].map(([k, v]) => (
            <div key={k} style={{ textAlign: "center" }}>
              <div style={{ fontSize: 15, fontWeight: 700, color: C.text }}>{k}</div>
              <div style={{ fontSize: 12, color: C.muted, marginTop: 2 }}>{v}</div>
            </div>
          ))}
        </div>
      </section>

      {/* ── Features ── */}
      <section id="features" style={{ padding: "96px 32px", maxWidth: 1100, margin: "0 auto" }}>
        <div style={{ marginBottom: 16 }}>
          <span style={S.sectionLabel}>Como funciona</span>
          <h2 style={S.h2}>O agente que trabalha<br />no teu repositório</h2>
          <p style={{ ...S.body, marginBottom: 40 }}>
            Do pedido em linguagem natural ao commit aprovado — tudo dentro do teu GitHub.
          </p>
        </div>

        <div style={{ display: "flex", gap: 40, alignItems: "flex-start", flexWrap: "wrap" }}>
          {/* Accordion */}
          <div style={{ flex: "0 0 340px", minWidth: 280 }}>
            {FEATURES.map((f, i) => (
              <div
                key={i}
                onClick={() => setFeatureIdx(i)}
                style={{
                  padding: "20px 20px",
                  borderRadius: 12,
                  marginBottom: 8,
                  cursor: "pointer",
                  background: featureIdx === i ? `${C.accent}18` : "transparent",
                  border: `1px solid ${featureIdx === i ? C.accent : "transparent"}`,
                  transition: "all 0.15s",
                }}
              >
                <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: featureIdx === i ? 8 : 0 }}>
                  <span style={{ fontSize: 18 }}>{f.icon}</span>
                  <span style={{ fontWeight: 600, fontSize: 14, color: featureIdx === i ? C.text : C.muted2 }}>
                    {f.title}
                  </span>
                </div>
                {featureIdx === i && (
                  <p style={{ fontSize: 13, color: C.muted2, lineHeight: 1.6, margin: 0 }}>{f.body}</p>
                )}
              </div>
            ))}
          </div>

          {/* Terminal */}
          <div style={{ flex: 1, minWidth: 300 }}>
            <TerminalMockup lines={currentFeature.terminal} />
          </div>
        </div>
      </section>

      {/* ── Pricing ── */}
      <section id="pricing" style={{ padding: "96px 32px", background: `${C.surface}80` }}>
        <div style={{ maxWidth: 1100, margin: "0 auto" }}>
          <div style={{ textAlign: "center", marginBottom: 48 }}>
            <span style={S.sectionLabel}>Preços</span>
            <h2 style={{ ...S.h2, textAlign: "center" }}>Simples, sem surpresas</h2>
            <p style={{ ...S.body, margin: "0 auto 32px", textAlign: "center" }}>
              Começa grátis. Escala conforme cresces.
            </p>

            {/* Individual / Enterprise tab */}
            <div style={{
              display: "inline-flex", background: C.dim, border: `1px solid ${C.border}`,
              borderRadius: 99, padding: 4, marginBottom: 32,
            }}>
              {[["individual", "Individual"], ["enterprise", "Enterprise"]].map(([v, l]) => (
                <button key={v} style={{
                  padding: "8px 24px", borderRadius: 99, fontSize: 13, fontWeight: 600,
                  border: "none", cursor: "pointer", transition: "all 0.15s",
                  background: v === "individual" ? C.surface : "transparent",
                  color: v === "individual" ? C.text : C.muted,
                  boxShadow: v === "individual" ? "0 1px 4px rgba(0,0,0,0.3)" : "none",
                }}>
                  {l}
                </button>
              ))}
            </div>

            {/* Mensal / Anual toggle */}
            <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 12 }}>
              <span style={{ fontSize: 13, color: billing === "monthly" ? C.text : C.muted, fontWeight: 600 }}>Mensal</span>
              <div
                onClick={() => setBilling(b => b === "monthly" ? "annual" : "monthly")}
                style={{
                  width: 44, height: 24, borderRadius: 99, cursor: "pointer",
                  background: billing === "annual" ? "#3b82f6" : C.dim,
                  border: `1px solid ${C.border}`,
                  position: "relative", transition: "background 0.2s",
                }}
              >
                <div style={{
                  position: "absolute", top: 3, left: billing === "annual" ? 22 : 3,
                  width: 16, height: 16, borderRadius: "50%", background: "#fff",
                  transition: "left 0.2s",
                }}/>
              </div>
              <span style={{ fontSize: 13, color: billing === "annual" ? C.text : C.muted, fontWeight: 600 }}>
                Anual
                <span style={{
                  marginLeft: 6, background: "#14532d", color: "#86efac",
                  fontSize: 10, fontWeight: 700, padding: "1px 6px", borderRadius: 99,
                }}>-15%</span>
              </span>
            </div>
          </div>

          <div style={{ display: "flex", gap: 20, flexWrap: "wrap" }}>
            {PLANS.map(p => (
              <PlanCard key={p.name} plan={p} billing={billing} onCta={onGetStarted} />
            ))}
          </div>

          {/* Credit packages */}
          <div style={{ marginTop: 64 }}>
            <h3 style={{ fontSize: 22, fontWeight: 700, color: C.text, marginBottom: 8, textAlign: "center" }}>
              Precisa de mais créditos?
            </h3>
            <p style={{ fontSize: 14, color: C.muted2, textAlign: "center", marginBottom: 32 }}>
              Compra créditos avulsos a qualquer momento — sem assinatura obrigatória.
            </p>
            <div style={{ display: "flex", gap: 16, flexWrap: "wrap", justifyContent: "center" }}>
              {[
                { label: "20 créditos", price: "€20", per: "€1.00/cr", highlight: false },
                { label: "50 créditos", price: "€49", per: "€0.98/cr", highlight: false, badge: "+2%" },
                { label: "120 créditos", price: "€99", per: "€0.83/cr", highlight: true, badge: "+20%" },
                { label: "300 créditos", price: "€189", per: "€0.63/cr", highlight: true, badge: "+30%" },
              ].map((pkg) => (
                <div key={pkg.label} style={{
                  background: C.surface, border: `1px solid ${pkg.highlight ? C.accent : C.border}`,
                  borderRadius: 12, padding: "20px 24px", minWidth: 160,
                  textAlign: "center", position: "relative",
                }}>
                  {pkg.badge && (
                    <div style={{
                      position: "absolute", top: -10, right: -6,
                      background: C.yellow, color: "#000", fontSize: 10, fontWeight: 700,
                      padding: "2px 8px", borderRadius: 99,
                    }}>{pkg.badge}</div>
                  )}
                  <div style={{ fontSize: 24, fontWeight: 800, color: C.text }}>{pkg.price}</div>
                  <div style={{ fontSize: 14, color: C.muted2, margin: "4px 0" }}>{pkg.label}</div>
                  <div style={{ fontSize: 11, color: C.muted }}>{pkg.per}</div>
                  <button
                    style={{ ...S.btn("outline"), marginTop: 12, width: "100%", fontSize: 12, padding: "7px 0" }}
                    onClick={onGetStarted}
                  >
                    Comprar
                  </button>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ── FAQ ── */}
      <section id="faqs" style={{ padding: "96px 32px" }}>
        <div style={{ maxWidth: 720, margin: "0 auto" }}>
          <div style={{ textAlign: "center", marginBottom: 48 }}>
            <span style={S.sectionLabel}>FAQ</span>
            <h2 style={{ ...S.h2, textAlign: "center" }}>Perguntas frequentes</h2>
          </div>
          {FAQS.map((f, i) => <FaqItem key={i} q={f.q} a={f.a} />)}
        </div>
      </section>

      {/* ── Enterprise ── */}
      <section id="enterprise" style={{ padding: "64px 32px" }}>
        <div style={{
          maxWidth: 900, margin: "0 auto", textAlign: "center",
          background: `linear-gradient(135deg, ${C.accent}18, ${C.surface})`,
          border: `1px solid ${C.accent}33`, borderRadius: 20, padding: "56px 40px",
        }}>
          <span style={S.sectionLabel}>Enterprise</span>
          <h2 style={{ ...S.h2, textAlign: "center" }}>Solução para equipas</h2>
          <p style={{ ...S.body, margin: "0 auto 32px", textAlign: "center" }}>
            Volume de créditos, SLA dedicado, SSO/SAML, logs de auditoria e
            contrato personalizado para a tua empresa.
          </p>
          <a
            href="mailto:enterprise@nexuscodes.org"
            style={{
              ...S.btn("primary"), padding: "13px 32px", fontSize: 15,
              display: "inline-block", textDecoration: "none",
            }}
          >
            Falar com vendas →
          </a>
        </div>
      </section>

      {/* ── CTA Final ── */}
      <section style={{
        padding: "96px 32px", textAlign: "center",
        background: `radial-gradient(ellipse 60% 80% at 50% 100%, ${C.accent}20 0%, transparent 70%)`,
      }}>
        <span style={S.sectionLabel}>Começa hoje</span>
        <h2 style={{ ...S.h2, fontSize: 48, textAlign: "center", maxWidth: 600, margin: "0 auto 16px" }}>
          Comece a construir com{" "}
          <span style={{
            background: `linear-gradient(135deg, ${C.accent}, ${C.accentH})`,
            WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent",
          }}>
            NEXUS CODE
          </span>{" "}
          HOJE.
        </h2>
        <p style={{ ...S.body, margin: "0 auto 40px", textAlign: "center" }}>
          5 créditos grátis no registo. Sem cartão de crédito.
        </p>
        <button
          style={{ ...S.btn("yellow"), padding: "16px 40px", fontSize: 16, fontWeight: 800, borderRadius: 12 }}
          onClick={onGetStarted}
        >
          Começar grátis →
        </button>
      </section>

      {/* ── Footer ── */}
      <footer style={{
        borderTop: `1px solid ${C.border}`,
        padding: "48px 32px 32px",
      }}>
        <div style={{ maxWidth: 1100, margin: "0 auto" }}>
          <div style={{ display: "flex", gap: 40, flexWrap: "wrap", marginBottom: 48 }}>
            {/* Brand */}
            <div style={{ flex: "0 0 220px" }}>
              <div style={{ fontSize: 16, fontWeight: 800, color: C.text, marginBottom: 12 }}>
                NEXUS <span style={{ color: C.accent }}>CODE</span>
              </div>
              <p style={{ fontSize: 13, color: C.muted, lineHeight: 1.7 }}>
                O agente de IA que constrói no teu repositório GitHub com controlo total.
              </p>
            </div>

            {[
              {
                title: "Produto",
                links: [
                  { label: "Construir", action: () => onGetStarted() },
                  { label: "Preços",    action: () => scrollTo("pricing") },
                  { label: "Integrações", action: () => scrollTo("features") },
                  { label: "Roadmap",   action: () => onGetStarted() },
                ],
              },
              {
                title: "Soluções",
                links: [
                  { label: "Enterprise",  action: () => scrollTo("enterprise") },
                  { label: "Startups",    action: () => onGetStarted() },
                  { label: "Agências",    action: () => onGetStarted() },
                  { label: "Freelancers", action: () => onGetStarted() },
                ],
              },
              {
                title: "Recursos",
                links: [
                  { label: "Documentação", action: () => onGetStarted() },
                  { label: "Tutoriais",    action: () => onGetStarted() },
                  { label: "Blog",         action: () => onGetStarted() },
                  { label: "Changelog",    action: () => onGetStarted() },
                ],
              },
              {
                title: "Empresa",
                links: [
                  { label: "Sobre",            action: () => onGetStarted() },
                  { label: "Carreiras",         action: () => onGetStarted() },
                  { label: "Termos de Serviço", action: () => onGetStarted() },
                  { label: "Privacidade",       action: () => onGetStarted() },
                ],
              },
            ].map(col => (
              <div key={col.title} style={{ flex: 1, minWidth: 140 }}>
                <div style={{ fontSize: 12, fontWeight: 700, color: C.text, marginBottom: 16, textTransform: "uppercase", letterSpacing: 1 }}>
                  {col.title}
                </div>
                <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                  {col.links.map(({ label, action }) => (
                    <span key={label} onClick={action} style={{ fontSize: 13, color: C.muted, cursor: "pointer", transition: "color 0.15s" }}
                      onMouseEnter={e => e.target.style.color = C.text}
                      onMouseLeave={e => e.target.style.color = C.muted}
                    >{label}</span>
                  ))}
                </div>
              </div>
            ))}
          </div>

          <div style={{
            display: "flex", justifyContent: "space-between", alignItems: "center",
            borderTop: `1px solid ${C.border}`, paddingTop: 24, flexWrap: "wrap", gap: 12,
          }}>
            <span style={{ fontSize: 12, color: C.muted }}>
              © {new Date().getFullYear()} NEXUS CODE. Todos os direitos reservados.
            </span>
            <span style={{ fontSize: 12, color: C.muted }}>
              nexuscodes.org
            </span>
          </div>
        </div>
      </footer>
    </div>
  );
}
